"use client";

import { Maximize2, X } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ExtraProps } from "streamdown";
import { TableCopyDropdown, TableDownloadDropdown } from "streamdown";

import { cn } from "@/lib/utils";

const TABLE_CLASSES = "w-full border-collapse border border-border";
// The icon size is pinned because streamdown draws its download icon at 16 and
// its copy icon at 14, which makes one button taller than its neighbours.
const ACTION_BUTTON_CLASSES =
  "flex cursor-pointer items-center justify-center rounded p-1 px-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [&_svg]:size-[14px]";

const TableFullscreen = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  return (
    <>
      <button
        aria-label="Expand table"
        className={ACTION_BUTTON_CLASSES}
        onClick={() => setIsOpen(true)}
        title="Expand table"
        type="button"
      >
        <Maximize2 size={14} />
      </button>
      {isOpen &&
        createPortal(
          <div
            aria-label="Table"
            aria-modal="true"
            className="fixed inset-0 z-50 flex flex-col gap-2 bg-background p-4"
            data-streamdown="table-wrapper"
            role="dialog"
          >
            <div className="flex items-center justify-end gap-1">
              <TableCopyDropdown />
              <TableDownloadDropdown />
              <button
                aria-label="Exit fullscreen"
                className={ACTION_BUTTON_CLASSES}
                onClick={() => setIsOpen(false)}
                title="Exit fullscreen"
                type="button"
              >
                <X size={14} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className={TABLE_CLASSES} data-streamdown="table">
                {children}
              </table>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

type MarkdownTableProps = ComponentProps<"table"> & ExtraProps;

export const MarkdownTable = ({
  children,
  className,
  node: _node,
  ...props
}: MarkdownTableProps) => (
  <div
    className="group/markdown-table relative my-4 flex flex-col space-y-2"
    data-streamdown="table-wrapper"
  >
    <div className="overflow-x-auto">
      <table
        className={cn(TABLE_CLASSES, className)}
        data-streamdown="table"
        {...props}
      >
        {children}
      </table>
    </div>
    <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 rounded-md border border-border bg-background/95 px-0.5 py-px opacity-0 shadow-sm transition-opacity group-hover/markdown-table:opacity-100 focus-within:opacity-100">
      <TableCopyDropdown className={ACTION_BUTTON_CLASSES} />
      <TableDownloadDropdown className={ACTION_BUTTON_CLASSES} />
      <TableFullscreen>{children}</TableFullscreen>
    </div>
  </div>
);

type CellProps<T extends keyof HTMLElementTagNameMap> = ComponentProps<T> &
  ExtraProps;

export const MarkdownTbody = ({
  children,
  className,
  node: _node,
  ...props
}: CellProps<"tbody">) => (
  <tbody
    className={cn("divide-y divide-border bg-muted/40", className)}
    data-streamdown="table-body"
    {...props}
  >
    {children}
  </tbody>
);

export const MarkdownTr = ({
  children,
  className,
  node: _node,
  ...props
}: CellProps<"tr">) => (
  <tr
    className={cn("border-border border-b", className)}
    data-streamdown="table-row"
    {...props}
  >
    {children}
  </tr>
);
